import { stopReasonTexts } from './stopReasons'
import type { HomePage, PageContent, Section } from '../types/pages'

type Json = unknown
const isRecord = (v: Json): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Vult ontbrekende velden aan uit de standaard, in de volgorde van de standaard (zo staan de
 * blokken in het beheer in de volgorde van de site). Wat er al staat wint altijd; lijsten worden
 * nooit samengevoegd; onbekende sleutels uit de opslag blijven bewaard (achteraan).
 */
export function withDefaults(saved: Json, defaults: Json): Json {
  if (!isRecord(saved) || !isRecord(defaults)) return saved === undefined ? structuredClone(defaults) : saved
  const out: Record<string, Json> = {}
  for (const key of Object.keys(defaults)) {
    out[key] = key in saved ? withDefaults(saved[key], defaults[key]) : structuredClone(defaults[key])
  }
  for (const key of Object.keys(saved)) {
    if (!(key in out)) out[key] = saved[key]
  }
  return out
}

/**
 * Oudere opslag van de homepage: het blok `salon` is opgegaan in
 * `story`. De woorden gaan mee; stappen, consultregel en beeld komen uit de standaard.
 */
function upgradeHome(home: Json, defaults: HomePage | undefined): Json {
  if (!isRecord(home) || !isRecord(home.salon)) return home
  const { salon, ...rest } = home as Record<string, Json> & { salon: Section }
  if (isRecord(rest.story)) return rest
  const base = defaults?.story
  const story = {
    ...(base ? structuredClone(base) : { steps: [], consultLine: '', media: { image: '', alt: '' } }),
    ...(salon.eyebrow !== undefined ? { eyebrow: salon.eyebrow } : {}),
    heading: salon.heading,
    ...(salon.body !== undefined ? { body: salon.body } : {}),
    ...(salon.cta !== undefined ? { cta: salon.cta } : {}),
    ...(salon.secondaryCta !== undefined ? { secondaryCta: salon.secondaryCta } : {}),
  }
  return { ...rest, story }
}

/**
 * Hero van vóór de twee deuren: de webshopknop (`secondaryCta`) wordt de knoptekst van de
 * webshopdeur; de salonregel (`salonLink`) vervalt, want de salondeur neemt die rol over.
 * De rest van de deuren komt uit de standaard (via `withDefaults`).
 */
function withDoors(home: Json): Json {
  if (!isRecord(home) || !isRecord(home.hero)) return home
  const hero = home.hero
  if (!('secondaryCta' in hero) && !('salonLink' in hero)) return home
  const { secondaryCta, ...rest } = hero
  delete rest.salonLink
  const shopDoor = isRecord(rest.shopDoor) ? rest.shopDoor : typeof secondaryCta === 'string' && secondaryCta ? { cta: secondaryCta } : rest.shopDoor
  return { ...home, hero: shopDoor === undefined ? rest : { ...rest, shopDoor } }
}

/**
 * Stappen van het salonverhaal van vóór de beelden per stap: elke stap krijgt een leeg beeld
 * (= het vaste salonbeeld op de site). Lijsten vult `withDefaults` bewust niet aan, dus hier.
 */
function withStepMedia(home: Json): Json {
  if (!isRecord(home) || !isRecord(home.story) || !Array.isArray(home.story.steps)) return home
  const steps = home.story.steps as Json[]
  if (steps.every((s) => !isRecord(s) || isRecord(s.media))) return home
  const next = steps.map((s) => (isRecord(s) && !isRecord(s.media) ? { ...s, media: { image: '', alt: '' } } : s))
  return { ...home, story: { ...home.story, steps: next } }
}

/**
 * Het blok "protocollen" werd "opbouwen" (een tijdlijn in plaats van protocolkaarten). Stond
 * er nog precies de oude standaardkop of -tekst, dan past die niet meer: die gaat weg, zodat
 * de nieuwe standaard (via `withDefaults`) erin komt. Zelf geschreven woorden blijven staan.
 */
const OLD_PROTOCOLS_HEADING = 'Het complete plan,'
const OLD_PROTOCOLS_BODY =
  'Complete routines voor drie tot vijf maanden: de juiste producten, in de juiste volgorde, met een huidplan op papier. Zoals ik ze in de salon opstel.'

function withBuildUp(home: Json): Json {
  if (!isRecord(home) || !isRecord(home.protocols)) return home
  const { heading, body, ...rest } = home.protocols
  const oldHeading = isRecord(heading) && heading.text === OLD_PROTOCOLS_HEADING
  const oldBody = body === OLD_PROTOCOLS_BODY
  if (!oldHeading && !oldBody) return home
  return {
    ...home,
    protocols: { ...rest, ...(oldHeading ? {} : { heading }), ...(oldBody || body === undefined ? {} : { body }) },
  }
}

/**
 * Oude standaardteksten die van rol veranderden. Staat er nog precies de oude standaard, dan
 * gaat die weg en vult `withDefaults` de nieuwe in; zelf geschreven woorden blijven staan.
 * - Zo werk ik: de knop is nu de afspraak; het huidadvies wordt de rustige link.
 * - Manifest: de link naar Over verhuisde naar Zo werk ik.
 * - Journal: de appregel werd een kaartje met kop, zin en knop.
 * - Webshopdeur: concreter, met het aantal producten en de verzendgrens.
 * - Opbouwen: de regel bij de cadeaus vervalt; de staffel staat nu bij de plank.
 */
const RETIRED: readonly { block: string; path: string[]; old: string; replace?: string }[] = [
  { block: 'story', path: ['quizCta'], old: 'Start het huidadvies' },
  { block: 'manifest', path: ['cta'], old: 'Waarom ik zo werk', replace: '' },
  { block: 'journal', path: ['askLine'], old: 'Staat je vraag er niet tussen? App {firstName}: {phone}' },
  { block: 'hero', path: ['shopDoor', 'body'], old: '{count} producten die hun werk in de salon bewezen. {freeShipping}.' },
  // De staffel verhuisde van Opbouwen naar de plank; daar zegt de kop het al (bestsellers.giftsTitle).
  { block: 'protocols', path: ['note'], old: 'Geldt voor elke bestelling, ook voor losse producten.' },
]

function withoutRetired(home: Json): Json {
  if (!isRecord(home)) return home
  let next: Record<string, Json> = home
  for (const r of RETIRED) {
    const block = next[r.block]
    if (!isRecord(block)) continue
    const parents = r.path.slice(0, -1)
    const leaf = r.path[r.path.length - 1] ?? ''
    let target: Json = block
    for (const p of parents) target = isRecord(target) ? target[p] : undefined
    if (!isRecord(target) || target[leaf] !== r.old) continue
    const cleared: Record<string, Json> = { ...target }
    if (r.replace === undefined) delete cleared[leaf]
    else cleared[leaf] = r.replace
    const rebuilt = parents.length === 0 ? cleared : setIn(block, parents, cleared)
    next = { ...next, [r.block]: rebuilt }
  }
  return next
}

function setIn(obj: Record<string, Json>, path: string[], value: Json): Record<string, Json> {
  const [head, ...tail] = path
  if (head === undefined) return obj
  const child = obj[head]
  return { ...obj, [head]: tail.length === 0 ? value : setIn(isRecord(child) ? child : {}, tail, value) }
}

/**
 * Oude standaardzinnen die niet meer kloppen, met hun vervanger. Alleen een tekst die nog
 * précies de oude standaard is, wordt vervangen (waar die ook staat, ook in lijsten); wat
 * Sarah zelf schreef blijft staan.
 * - Het huidplan staat in Mijn omgeving, niet op papier.
 * - Over: "geen wisselende specialisten" klopt niet meer zodra er een collega meewerkt.
 */
const OPBOUWEN_STEP =
  'Stap voor stap introduceren, met een huidplan dat je altijd terugvindt in Mijn omgeving. Na drie weken vraag ik hoe het gaat en stuur ik bij.'

export const REPLACED_DEFAULTS: Readonly<Record<string, string>> = {
  'Stap voor stap introduceren, met een huidplan op papier. Na drie weken vraag ik hoe het gaat en sturen we bij.': OPBOUWEN_STEP,
  // De grote doorloop (docs/VORMGEVING.md › Woorden): ik-vorm, vaste woorden (afspraak · huidtest ·
  // protocol) en geen beloftes die de collega tegenspreken ("volgens mijn werkwijze").
  'Stap voor stap introduceren, met een huidplan dat je altijd terugvindt in Mijn omgeving. Na drie weken vraag ik hoe het gaat en sturen we bij.':
    OPBOUWEN_STEP,
  'Plan een behandeling': 'Plan een afspraak',
  'Plan {consultName}': 'Plan een afspraak',
  'Maak kennis in de salon': 'Plan een afspraak',
  'Start het huidadvies': 'Doe de huidtest',
  'Twijfel je? Huidadvies in één minuut': 'Doe de huidtest',
  'Welk protocol past bij mij?': 'Doe de huidtest',
  'Liever eerst het huidadvies': 'Of doe eerst de huidtest',
  'Twijfel je tussen twee klachten? Het huidadvies kiest met je mee.': 'Twijfel je tussen twee klachten? De huidtest kiest met je mee.',
  'App dat je later bent': 'Ik ben later',
  'Stel je vraag via WhatsApp': 'Vraag het via WhatsApp',
  'De fitnessoefening voor je huid': 'Training voor je huid',
  'Altijd behandeld door': 'Behandeld volgens de werkwijze van',
  'Behandelingen en huidanalyse in {city}, altijd door {firstName} zelf.': 'Behandelingen en huidanalyse in {city}, volgens mijn werkwijze.',
  'Eén behandelkamer, één cliënt tegelijk, altijd door mij. In drie stappen naar een routine die bij je past, in de salon of online.':
    'Eén behandelkamer, één cliënt tegelijk, volgens mijn werkwijze. In drie stappen naar verzorging die bij je past, in de salon of online.',
  'Ik kijk naar je huid, je routine en je leven: hormonen, slaap, stress. Pas daarna praten we over producten.':
    'Ik kijk naar je huid, je routine en je leven: hormonen, slaap, stress. Pas daarna kies ik producten.',
  'Een kort bericht van mij. Prikt iets, of gaat het te langzaam? Dan sturen we samen bij.':
    'Een kort bericht van mij. Prikt iets, of gaat het te langzaam? Dan stuur ik bij.',
  'Je huid is veranderd, dus je routine mag mee. Bestel bij, of kom langs voor een nieuwe huidanalyse.':
    'Je huid is veranderd, dus je verzorging mag mee. Bestel bij, of kom langs voor een nieuwe huidanalyse.',
  'Ik lees en beantwoord alles zelf, binnen {responseHours} uur. Ook als je (nog) niets koopt.':
    'Ik antwoord meestal dezelfde dag, altijd binnen {responseHours} uur. Ook als je (nog) niets koopt.',
  // Direct naar de tekst van nu (geen keten): "Alleen op afspraak" staat er inmiddels vast voor.
  'Alleen op afspraak. Ik behandel één cliënt tegelijk en kan tussendoor niet opendoen.':
    'Er is één cliënt tegelijk binnen, dus tussendoor gaat de deur niet open.',
  'Bijna elke behandeling draait om bindweefselmassage: fitness voor je huid. Daaromheen werk ik aan wat je huid die dag nodig heeft, van pigment en roodheid tot verslapping. Resultaat zie je vaak binnen 60 minuten.':
    'Bijna elke behandeling draait om bindweefselmassage: training voor je huid. Daaromheen werk ik aan wat je huid die dag nodig heeft, van pigment en roodheid tot verslapping. Resultaat zie je vaak binnen 60 minuten.',
  'Remt pigment, geeft glow en vangt vrije radicalen weg. Overdag, onder je SPF.':
    'Remt pigment, geeft glans en vangt vrije radicalen weg. Overdag, onder je SPF.',
  'Hier deel ik mijn tips': 'Wat werkt er nu',
  'en bevindingen.': 'écht voor je huid?',
  // Het oude stockbeeld bij "kom langs" wordt het sfeerbeeld van de behandelkamer (docs/BEELDEN.md, S/O).
  'https://cdn.magicpatterns.com/patterns/generated-images/7166f87a-c402-483f-8556-b060455341f8.jpg':
    'https://cdn.magicpatterns.com/patterns/generated-images/12880a60-c23b-49bb-b836-2609a96a26cb.jpg',
  'Sfeerbeeld: een behandelbed met linnen in daglicht': 'Sfeerbeeld: een rustige behandelkamer met linnen in daglicht',
  'Je huidplan op papier': 'Je huidplan in Mijn omgeving',
  'Welk product wanneer, en hoe je ze stap voor stap introduceert. De cadeaus die bij je bedrag horen, zitten erbij.':
    'Welk product wanneer, en hoe je ze stap voor stap introduceert. Altijd bij de hand, ook op je telefoon, en ik werk het bij als je huid verandert.',
  'Neem het protocol dat ik zou opstellen: dezelfde prijzen, maar in de juiste volgorde en met een huidplan op papier.':
    'Neem het protocol dat ik zou opstellen: dezelfde prijzen, maar in de juiste volgorde en met een persoonlijk huidplan in Mijn omgeving.',
  'De producten die samenwerken, in de juiste volgorde, met een huidplan op papier.':
    'De producten die samenwerken, in de juiste volgorde, met een huidplan in Mijn omgeving.',
  'Geen wachtkamer vol mensen, geen wisselende specialisten. Kom een paar minuten eerder: er staat thee klaar.':
    'Geen wachtkamer vol mensen en geen haast. Kom een paar minuten eerder: er staat thee klaar.',
  // Derde doorloop: dubbelingen eruit (docs/VORMGEVING.md › Bewust dubbel).
  'Huidspecialist & vegan skincare in {city}': 'Huidspecialist in {city}',
  'Boek een bindweefselmassage': 'Boek deze behandeling',
  'Alleen op afspraak. Er is één cliënt tegelijk binnen, dus tussendoor gaat de deur niet open.':
    'Er is één cliënt tegelijk binnen, dus tussendoor gaat de deur niet open.',
  'Een behandeling in de salon': 'Een behandeling of online consult',
  'Kies online een dag en tijd die jou past.': 'Kies een dag en tijd die je past: in de salon, of {consultMinutes} minuten online voor {consultPrice}.',
  'Huidspecialist in Amsterdam': 'Over mij',
  'Ik verkoop niets wat ik niet zelf gebruik. Deze staan al jaren naast mijn kraan, en in de salon.':
    'Ik verkoop niets wat ik niet zelf gebruik. Deze drie staan al jaren naast mijn kraan.',
  '{city}, één behandelkamer.': 'Kom een keer kennismaken.',
  'Kom kennismaken': 'De salon in {city}',
  'Wat er kosteloos bij komt': 'Wat je er cadeau bij krijgt',
  // Vierde doorloop: vergelijken staat nu in een uitklapper onder de klachtkiezer.
  'In één oogopslag': 'Alle protocollen naast elkaar',
}

/** De standaardkop en -zin van het oude blok "overige protocollen" op Protocollen. */
const OLD_OTHERS = {
  heading: { text: 'Of begin met wat nu het meest stoort' },
  body: 'Drie à vier producten, ongeveer drie maanden. Uit te breiden zodra je huid eraan gewend is.',
}

/**
 * Protocollen: het blok "overige protocollen" is de klachtkiezer geworden (`concerns`). Had Sarah
 * de woorden zelf aangepast, dan verhuizen ze mee; de standaard maakt plaats voor de nieuwe.
 */
function withRoutinesConcerns(routines: Json): Json {
  if (!isRecord(routines) || routines.concerns !== undefined || !isRecord(routines.others)) return routines
  if (JSON.stringify(routines.others) === JSON.stringify(OLD_OTHERS)) return routines
  return { ...routines, concerns: routines.others }
}

/**
 * De alinea's van Over zoals ze standaard waren. Staan ze er nog precies zo, dan komt het nieuwe
 * verhaal ervoor in de plaats (opening · hoe het begon · slotzin); zelf geschreven alinea's blijven.
 */
const OLD_ABOUT_PARAGRAPHS = [
  'Ruim {years} jaar geleden begon ik in een klassieke salon waar iedereen dezelfde behandeling kreeg. Dat wringde. Sindsdien werk ik met mijn eigen behandelingen: bindweefselmassage, ontspanningsmassage en energetisch werk. Een hormonale huid vraagt iets anders dan een huid in de overgang. Beide vragen tijd.',
  'In {city} zie ik één cliënt tegelijk. Ik werk met emerginC, cosmeceuticals die bijna medicinaal zijn, zodat je huid echt verbetert.',
  'Ik beloof geen wonderen. Wel eerlijkheid: over wat haalbaar is, hoe lang het duurt en wanneer je beter naar een arts gaat.',
]

function withNewAboutStory(about: Json, defaults: PageContent['about'] | undefined): Json {
  if (!defaults || !isRecord(about) || !isRecord(about.hero)) return about
  if (JSON.stringify(about.hero.paragraphs) !== JSON.stringify(OLD_ABOUT_PARAGRAPHS)) return about
  return { ...about, hero: { ...about.hero, paragraphs: [...defaults.hero.paragraphs] } }
}

/** Vervangt overal precies-oude standaardzinnen (zie `REPLACED_DEFAULTS`). Puur. */
export function withReplacedDefaults(value: Json): Json {
  if (typeof value === 'string') return REPLACED_DEFAULTS[value] ?? value
  if (Array.isArray(value)) return value.map(withReplacedDefaults)
  if (!isRecord(value)) return value
  const out: Record<string, Json> = {}
  for (const [k, v] of Object.entries(value)) out[k] = withReplacedDefaults(v)
  return out
}

/**
 * Velden die de site niet meer leest. Ze verdwijnen uit de opslag, zodat het beheer geen veld
 * toont dat niets doet. De woorden zijn op de site al vervangen door een vaste tekst of een ander veld.
 * - Protocollen: de webshopknop in de opening (de hoofdknop ligt vast) en de kop boven de vragen
 *   (de vragen zijn één gedeeld blok met één kop).
 * - Over: de tweede knop in de opening (de hoofdknop "Bekijk de collectie" ligt vast).
 * - Protocollen: de huidtest onder "klein beginnen" (staat al in de opening).
 * - Contact: het kanaal "advies" (één kanaal voor behandeling en consult, met één knop).
 * - Protocollen: "zo werkt een protocol" (kop en stappen; de tijdlijn van Home neemt het over) en
 *   "overige protocollen" (de klachtkiezer; eigen woorden verhuizen eerst naar `concerns`).
 * - Over: het blok "wie het ook gebruiken" (Over gaat alleen over Sarah; de perslijn staat in het verhaal).
 */
export const DEAD_FIELDS: readonly (readonly string[])[] = [
  ['routines', 'hero', 'secondaryCta'],
  ['routines', 'faqTitle'],
  ['routines', 'smallStart', 'quizCta'],
  ['about', 'hero', 'secondaryCta'],
  ['contact', 'channels', 'advice'],
  ['routines', 'howHeading'],
  ['routines', 'steps'],
  ['routines', 'others'],
  ['about', 'clients'],
]

function withoutDeadFields(value: Record<string, Json>): Record<string, Json> {
  let next = value
  for (const path of DEAD_FIELDS) {
    const parents = path.slice(0, -1)
    const leaf = path[path.length - 1] ?? ''
    let target: Json = next
    for (const p of parents) target = isRecord(target) ? target[p] : undefined
    if (!isRecord(target) || !(leaf in target)) continue
    const cleared: Record<string, Json> = { ...target }
    delete cleared[leaf]
    next = parents.length === 0 ? cleared : setIn(next, parents, cleared)
  }
  return next
}

/**
 * Paginateksten uit een oudere opslag in de vorm van nu brengen, vóórdat de site of het beheer
 * ze gebruikt. Puur; geeft hetzelfde object terug als er niets te doen is. Er gaat niets stil
 * verloren: omgezette woorden verhuizen mee, onbekende velden blijven staan.
 *
 * `defaults` = de standaardteksten (`pageDefaults` uit data/pages.ts). Zonder: alleen omzetten.
 */
export function upgradePageContent(p: PageContent, defaults?: PageContent): PageContent {
  const raw = p as unknown as Record<string, Json>
  let next: Record<string, Json> = { ...raw }

  if (raw.home !== undefined) next.home = withoutRetired(withBuildUp(withDoors(withStepMedia(upgradeHome(raw.home, defaults?.home)))))
  if (raw.about !== undefined) next.about = withNewAboutStory(raw.about, defaults?.about)
  if (raw.routines !== undefined) next.routines = withRoutinesConcerns(raw.routines)

  const portal = raw.portal as PageContent['portal'] | undefined
  if (portal) {
    const texts = stopReasonTexts(portal.stopReasons)
    if (JSON.stringify(texts) !== JSON.stringify(portal.stopReasons)) next.portal = { ...portal, stopReasons: texts }
  }

  next = withoutDeadFields(withReplacedDefaults(next) as Record<string, Json>)
  if (defaults) next = withDefaults(next, defaults) as Record<string, Json>

  return JSON.stringify(next) === JSON.stringify(raw) ? p : (next as unknown as PageContent)
}
